// BUILD-042 UAT local-usability cleanup (2026-10-05 decision), item #2 — a single shared
// Personal & Contacts panel used by both My HR (self) and HR Administration (any Employee),
// replacing the two near-duplicate stacked-forms versions that previously lived separately in
// MyHrTab.tsx and HrAdminTab.tsx. Behavior per the decision:
//   "saved values normally render as readable details; explicit Edit opens fields, Save
//   persists then returns to reading, Cancel discards unsaved changes... prevent accidental
//   duplicate actions, preserve entered values after failed save... Emergency contacts need an
//   understandable correction path..."
// Server authorization/validation/history are UNCHANGED — this is a presentation-layer
// reorganization over the existing getHrPersonalDetail/upsertHrPersonalDetail/
// createHrEmergencyContact/archiveHrEmergencyContact API, used identically by self and admin
// callers (the backend already scopes 'self' vs 'company' — this component does not decide
// that, it just calls the same functions MyHrTab/HrAdminTab already called).
import { useEffect, useId, useState } from 'react';
import {
  getHrPersonalDetail, upsertHrPersonalDetail, createHrEmergencyContact, archiveHrEmergencyContact,
  listHrPersonalDetailHistory,
  type HrPersonalDetail, type HrEmergencyContact,
} from '../../api/client';
import { formatLocalDateTime, resolveDisplayName } from './hrFormat';
import { CollapsibleHelp } from './CollapsibleHelp';
import { CardTitle } from './workspace/SectionHeading';

type FormState = {
  personalEmail: string; personalPhone: string; addressLine1: string; addressCity: string;
  addressState: string; addressZip: string; familyReferenceNote: string;
};
const EMPTY_FORM: FormState = { personalEmail: '', personalPhone: '', addressLine1: '', addressCity: '', addressState: '', addressZip: '', familyReferenceNote: '' };

function toForm(d: HrPersonalDetail | null): FormState {
  return {
    personalEmail: d?.personalEmail ?? '', personalPhone: d?.personalPhone ?? '',
    addressLine1: d?.addressLine1 ?? '', addressCity: d?.addressCity ?? '',
    addressState: d?.addressState ?? '', addressZip: d?.addressZip ?? '',
    familyReferenceNote: d?.familyReferenceNote ?? '',
  };
}

/** Readable "Line 1, City, State ZIP" — never a label the user has to assemble mentally from
 *  four separate blank-looking fields when nothing has been entered yet. */
function formatAddress(d: HrPersonalDetail | null): string | null {
  if (!d) return null;
  const line1 = d.addressLine1?.trim();
  const cityStateZip = [d.addressCity, d.addressState].filter(Boolean).join(', ') + (d.addressZip ? ` ${d.addressZip}` : '');
  const parts = [line1, cityStateZip.trim()].filter((p) => p && p.length > 0);
  return parts.length > 0 ? parts.join(', ') : null;
}

export function PersonalContactsPanel({
  employeeId, nameMap, isSelf, section = 'both',
}: {
  employeeId: string;
  nameMap?: Record<string, string | null>;
  isSelf?: boolean;
  /** UAT visual cleanup (2026-10-05): My HR's new per-tab layout wants Contact Details and
   *  Emergency Contacts as two separate destinations instead of one combined panel. Both still
   *  read from the SAME getHrPersonalDetail() response — this only controls which half of this
   *  component's existing (already-working) markup renders; no new fetch logic, no behavior
   *  change for existing callers (HrAdminTab's Employee detail "contacts" tab keeps the default
   *  'both' and is unaffected). */
  section?: 'both' | 'contact' | 'emergency';
}) {
  const emailId = useId();
  const phoneId = useId();
  const line1Id = useId();
  const cityId = useId();
  const stateId = useId();
  const zipId = useId();
  const familyId = useId();

  const [detail, setDetail] = useState<HrPersonalDetail | null>(null);
  const [contacts, setContacts] = useState<HrEmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<Awaited<ReturnType<typeof listHrPersonalDetailHistory>>['history'] | null>(null);

  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', relationship: '', phone: '', isPrimary: false });
  const [contactError, setContactError] = useState<string | null>(null);

  async function fetchDetail(): Promise<void> {
    const d = await getHrPersonalDetail(employeeId);
    setDetail(d.personalDetail);
    setContacts(d.emergencyContacts);
  }
  function refresh() {
    setLoading(true);
    fetchDetail()
      .then(() => setError(null))
      .catch((e: any) => setError(e.message || 'Could not load personal details.'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { refresh(); }, [employeeId]);

  function startEdit() {
    setForm(toForm(detail));
    setSaveError(null);
    setEditing(true);
  }
  function cancelEdit() {
    setEditing(false);
    setSaveError(null);
    // Deliberately does NOT touch `detail` — Cancel discards only the in-progress form state,
    // never anything already saved.
  }
  async function handleSave() {
    if (saving) return; // prevent accidental duplicate submit
    setSaving(true);
    setSaveError(null);
    try {
      await upsertHrPersonalDetail(employeeId, form);
      await fetchDetail(); // refetch so view mode shows the real saved row, not just the form echo
      setEditing(false);
    } catch (e: any) {
      // Entered values are preserved — `form` state is untouched on failure, so the admin never
      // has to retype anything after a transient error.
      setSaveError(e.message || 'Failed to save. Your entries were not lost — try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddContact() {
    if (addingContact) return;
    if (!newContact.name.trim() || !newContact.phone.trim()) {
      setContactError('Name and phone are required.');
      return;
    }
    setAddingContact(true);
    setContactError(null);
    try {
      await createHrEmergencyContact(employeeId, {
        name: newContact.name.trim(), relationship: newContact.relationship.trim() || undefined,
        phone: newContact.phone.trim(), isPrimary: newContact.isPrimary,
      });
      setNewContact({ name: '', relationship: '', phone: '', isPrimary: false });
      refresh();
    } catch (e: any) {
      setContactError(e.message || 'Failed to add contact.');
    } finally {
      setAddingContact(false);
    }
  }
  async function handleRemoveContact(contactId: string) {
    await archiveHrEmergencyContact(employeeId, contactId);
    refresh();
  }

  function toggleHistory() {
    if (showHistory) { setShowHistory(false); return; }
    setShowHistory(true);
    if (history === null) {
      listHrPersonalDetailHistory(employeeId)
        .then((d) => setHistory(d.history))
        .catch(() => setHistory([])); // NEEDS DEV DEPLOY until migration/route ships — see client.ts
    }
  }

  if (loading) return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-400">Loading personal details…</div>;
  if (error) return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-red-700">{error}</div>;

  const address = formatAddress(detail);

  const showContact = section === 'both' || section === 'contact';
  const showEmergency = section === 'both' || section === 'emergency';

  return (
    <div className="space-y-3">
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      {showContact && (
      <>
      <CardTitle
        action={!editing ? (
          <button type="button" onClick={startEdit} className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-1.5 rounded-md">
            Edit {section === 'contact' ? 'personal contacts' : 'contact details'}
          </button>
        ) : undefined}
      >
        {section === 'contact' ? 'Contact details' : 'Personal & Contact Details'}
      </CardTitle>
      {/* BUILD-042 My HR self-service & change-request decision (2026-10-06): "Personal contact
          email changes must never silently change login identity, work email or account
          recovery settings" and "An HR contact address is not proof that payroll/benefits
          addresses changed; no downstream synchronization is authorized." These are REAL,
          already-true facts about this existing route (personalEmail/address live only in
          hr_personal_detail, with no code path touching Cognito or payroll — confirmed by
          investigation) — stated here so that is visible to the person editing, not just true
          in the backend. */}
      <p className="text-xs text-gray-400 mb-3">
        This personal email and address are separate from your UniqueHQ login and work email, and from any payroll/benefits address — changing them here never updates those.
      </p>

      {!editing ? (
        // 2026-10-13 UAT: match Personal details' InfoItem hierarchy — a small muted (text-xs)
        // label, then a clearly larger/bolder value with a little vertical gap, so the label and
        // value no longer read as one blob.
        <dl className="grid grid-cols-2 gap-5">
          <div><dt className="text-xs text-gray-500">Personal email</dt><dd className="text-sm font-bold text-gray-900 mt-0.5">{detail?.personalEmail || <span className="text-gray-400 font-normal">Not on file</span>}</dd></div>
          <div><dt className="text-xs text-gray-500">Personal phone</dt><dd className="text-sm font-bold text-gray-900 mt-0.5">{detail?.personalPhone || <span className="text-gray-400 font-normal">Not on file</span>}</dd></div>
          <div className="col-span-2"><dt className="text-xs text-gray-500">Address</dt><dd className="text-sm font-bold text-gray-900 mt-0.5">{address || <span className="text-gray-400 font-normal">Not on file</span>}</dd></div>
          <div className="col-span-2">
            <dt className="text-xs text-gray-500">Family / next-of-kin reference</dt>
            <dd className="text-sm font-bold text-gray-900 mt-0.5 whitespace-pre-wrap">{detail?.familyReferenceNote || <span className="text-gray-400 font-normal">Not on file</span>}</dd>
          </div>
        </dl>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1"><label htmlFor={emailId} className="text-xs text-gray-500">Personal email</label><input id={emailId} type="email" className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={form.personalEmail} onChange={(e) => setForm((f) => ({ ...f, personalEmail: e.target.value }))} /></div>
          <div className="flex flex-col gap-1"><label htmlFor={phoneId} className="text-xs text-gray-500">Personal phone</label><input id={phoneId} type="tel" className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={form.personalPhone} onChange={(e) => setForm((f) => ({ ...f, personalPhone: e.target.value }))} /></div>
          <div className="col-span-2 flex flex-col gap-1"><label htmlFor={line1Id} className="text-xs text-gray-500">Address</label><input id={line1Id} className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={form.addressLine1} onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))} /></div>
          <div className="flex flex-col gap-1"><label htmlFor={cityId} className="text-xs text-gray-500">City</label><input id={cityId} className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={form.addressCity} onChange={(e) => setForm((f) => ({ ...f, addressCity: e.target.value }))} /></div>
          <div className="flex gap-2">
            <div className="flex flex-col gap-1 flex-1"><label htmlFor={stateId} className="text-xs text-gray-500">State</label><input id={stateId} className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={form.addressState} onChange={(e) => setForm((f) => ({ ...f, addressState: e.target.value }))} /></div>
            <div className="flex flex-col gap-1 flex-1"><label htmlFor={zipId} className="text-xs text-gray-500">ZIP</label><input id={zipId} className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={form.addressZip} onChange={(e) => setForm((f) => ({ ...f, addressZip: e.target.value }))} /></div>
          </div>
          <div className="col-span-2 flex flex-col gap-1">
            <label htmlFor={familyId} className="text-xs text-gray-500">Family / next-of-kin reference (optional — not an emergency contact or dependent record)</label>
            <textarea id={familyId} className="border border-gray-300 rounded px-3 py-1.5 text-sm" rows={2} value={form.familyReferenceNote} onChange={(e) => setForm((f) => ({ ...f, familyReferenceNote: e.target.value }))} />
          </div>
          <div className="col-span-2 flex items-center gap-2 pt-1">
            <button onClick={handleSave} disabled={saving} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={cancelEdit} disabled={saving} className="text-sm text-gray-500 px-4 py-1.5 disabled:opacity-50">Cancel</button>
            {saveError && <span className="text-sm text-red-700">{saveError}</span>}
          </div>
        </div>
      )}

      {detail?.updatedAt && (
        <div className="text-xs text-gray-400 mt-3">
          Last updated {formatLocalDateTime(detail.updatedAt)} by {resolveDisplayName(detail.updatedByEmployeeId, nameMap)}
          {' · '}
          <button type="button" onClick={toggleHistory} className="text-[#4AABDE] hover:underline">
            {showHistory ? 'Hide change history' : 'View change history'}
          </button>
        </div>
      )}
      {showHistory && (
        <div className="mt-2 border-t border-gray-100 pt-2 space-y-1.5">
          {history === null ? (
            <div className="text-xs text-gray-400">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-xs text-gray-400 italic">No prior changes recorded.</div>
          ) : (
            history.map((h) => (
              <div key={h.historyId} className="text-xs text-gray-500">
                Previously: {h.personalEmail || '—'} · {h.personalPhone || '—'} — changed by {resolveDisplayName(h.changedByEmployeeId, nameMap)} on {formatLocalDateTime(h.changedAt)}
              </div>
            ))
          )}
        </div>
      )}
      </>
      )}

      {showEmergency && (
      <>
      <div className={section === 'emergency' ? '' : 'mt-6'}><CardTitle>Emergency contacts</CardTitle></div>
      {contacts.length === 0 ? (
        <div className="text-sm text-gray-400 mb-3">No emergency contacts on file.</div>
      ) : (
        <ul className="divide-y divide-gray-100 mb-3">
          {contacts.map((c) => (
            <li key={c.contactId} className="flex items-center justify-between py-2 text-sm">
              <div>
                <span className="font-medium text-gray-900">{c.name}</span>
                {c.relationship && <span className="text-gray-400"> · {c.relationship}</span>}
                <span className="text-gray-400"> · {c.phone}</span>
                {c.isPrimary && <span className="ml-2 text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">Primary</span>}
              </div>
              {/* "Remove" archives (soft-delete) — the understandable correction path the
                  decision asks for: to CORRECT a contact's phone/name, remove the old one and
                  add the corrected version, rather than silently overwriting it in place. This
                  preserves history (archived rows are never deleted) while staying a single,
                  obvious action rather than a separate inline-edit mode for a short list. */}
              <button onClick={() => handleRemoveContact(c.contactId)} className="text-red-600 text-xs" title="Remove this contact. To correct a mistake, remove it and add the corrected version — the removed entry is kept in history, never deleted.">
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2 flex-wrap items-end">
        <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Name" value={newContact.name} onChange={(e) => setNewContact((c) => ({ ...c, name: e.target.value }))} />
        <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Relationship" value={newContact.relationship} onChange={(e) => setNewContact((c) => ({ ...c, relationship: e.target.value }))} />
        <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Phone" value={newContact.phone} onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value }))} />
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={newContact.isPrimary} onChange={(e) => setNewContact((c) => ({ ...c, isPrimary: e.target.checked }))} />
          Primary
        </label>
        <button onClick={handleAddContact} disabled={addingContact} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
          {addingContact ? 'Adding…' : 'Add Contact'}
        </button>
        {contactError && <span className="text-sm text-red-700">{contactError}</span>}
      </div>
      </>
      )}
    </div>

    {/* UAT visual cleanup (2026-10-05, Owner-directed): "How this page works" moved to its own
        card below this page's content, instead of a small link above it — see
        CollapsibleHelp.tsx's own header for the full rationale. */}
    {showContact && (
      <CollapsibleHelp>
        <p>
          {isSelf
            ? 'Only you and HR Administration can see this information. It is never visible to a manager or company directory.'
            : "This is the Employee's private contact information. It is visible to HR Administration and to the Employee themselves — never to a manager or the general company directory."}
        </p>
        <p>Every change here is recorded with who changed it and when (see "Change history" above) — nothing is overwritten silently.</p>
      </CollapsibleHelp>
    )}
    {showEmergency && section === 'emergency' && (
      <CollapsibleHelp>
        <p>People to contact if something happens to {isSelf ? 'you' : 'this Employee'} at work. Visible to HR Administration and {isSelf ? 'you' : 'the Employee'} only.</p>
      </CollapsibleHelp>
    )}
    </div>
  );
}
